import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * İçindekiler — article içindeki <h2>/<h3> başlıklarından otomatik üretir.
 * Scroll spy ile aktif başlığı vurgular.
 *
 * Desktop-only kullanılıyor (parent <aside className="hidden lg:block">).
 *
 * Article container'a `id="blog-article"` verilmeli — bu id'li elemanı tarar.
 */
export function TableOfContents({ articleSelector = "#blog-article" }) {
  const { t } = useLanguage();
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const article = document.querySelector(articleSelector);
    if (!article) return;

    // Başlıkları topla, id yoksa slug ata
    const nodes = Array.from(article.querySelectorAll("h2, h3"));
    const list = nodes.map((node, i) => {
      if (!node.id) {
        const slug = (node.textContent || `heading-${i}`)
          .toLowerCase()
          .replace(/[^a-z0-9\u00C0-\u024F\u0600-\u06FF]+/gi, "-")
          .replace(/^-+|-+$/g, "")
          .substring(0, 60);
        node.id = slug || `heading-${i}`;
      }
      return {
        id: node.id,
        text: node.textContent || "",
        level: node.tagName === "H2" ? 2 : 3,
      };
    });
    setHeadings(list);

    if (list.length === 0) return;

    // Scroll spy
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "0px 0px -70% 0px", threshold: 0 }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [articleSelector]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label={t("blog.toc")} className="sticky top-28">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-xs font-bold tracking-[0.15em] text-slate-500 mb-3">
          {t("blog.toc")}
        </h3>
        <ul className="space-y-1.5 text-sm">
          {headings.map((h) => (
            <li key={h.id} className={h.level === 3 ? "ml-3" : ""}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(h.id);
                  if (el) {
                    window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
                  }
                }}
                className={`block py-1 border-l-2 pl-3 transition-colors ${
                  activeId === h.id
                    ? "border-[#22A7D1] text-[#22A7D1] font-medium"
                    : "border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400"
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
